import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { lazy, Suspense, useEffect } from "react";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { WhatsAppLeadModal } from "./components/WhatsAppLeadModal";
import { GoogleAnalytics } from "./components/GoogleAnalytics";
import { ThemeProvider } from "./contexts/ThemeContext";
import { usePageTracking } from "./hooks/usePageTracking";
import Home from "./pages/Home";

// Subpáginas com code-splitting para manter a Home leve
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const Admin = lazy(() => import("./pages/Admin"));
const Servicos = lazy(() => import("./pages/Servicos"));
const Aplicacoes = lazy(() => import("./pages/Aplicacoes"));
const SegmentoDetail = lazy(() => import("./pages/SegmentoDetail"));
const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));
const JunteSe = lazy(() => import("./pages/JunteSe"));
const Parceiros = lazy(() => import("./pages/Parceiros"));
const TrabalheCo = lazy(() => import("./pages/TrabalheCo"));
const TermosDeUso = lazy(() => import("./pages/TermosDeUso"));
const FAQPage = lazy(() => import("./pages/FAQ"));
const Tecnologias = lazy(() => import("./pages/Tecnologias"));
const Loja = lazy(() => import("./pages/Loja"));
const EquiSensePage = lazy(() => import("./pages/tecnologias/EquiSensePage"));
const EquiCorexPage = lazy(() => import("./pages/tecnologias/EquiCorexPage"));
const ManutencaoPreventiva = lazy(() => import("./pages/servicos/ManutencaoPreventiva"));
const ManutencaoCorretiva = lazy(() => import("./pages/servicos/ManutencaoCorretiva"));
const ContratosManutencao = lazy(() => import("./pages/servicos/ContratosManutencao"));
const InstalacaoRemanejo = lazy(() => import("./pages/servicos/InstalacaoRemanejo"));
const Retrofit = lazy(() => import("./pages/servicos/Retrofit"));
const SuporteTecnico = lazy(() => import("./pages/servicos/SuporteTecnico"));
const Clientes = lazy(() => import("./pages/Clientes"));
const PoliticaDevolucao = lazy(() => import("./pages/PoliticaDevolucao"));
const ConviteAceitar = lazy(() => import("./pages/ConviteAceitar"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const PasswordRecovery = lazy(() => import("./pages/PasswordRecovery"));
const PasswordReset = lazy(() => import("./pages/PasswordReset"));
const SlidingDoorLanding = lazy(() => import("./pages/SlidingDoorLanding"));
const EclusaRemoteInspectionLanding = lazy(() => import("./pages/EclusaRemoteInspectionLanding"));
const CategorySolutions = lazy(() => import("./pages/CategorySolutions"));
const TechnicianRegistration = lazy(() => import("./pages/TechnicianRegistration"));
const Monitoring = lazy(() => import("./pages/Monitoring"));

function RedirectToParceiros() {
  const [, setLocation] = useLocation();
  useEffect(() => { setLocation("/parceiros"); }, [setLocation]);
  return <div className="min-h-screen bg-white" aria-busy="true" />;
}

function PageFallback() {
  return <div className="min-h-screen bg-white" aria-busy="true" />;
}

function Router() {
  usePageTracking();
  return (
    <Suspense fallback={<PageFallback />}>
      <Switch>
        <Route path={"/monitoramento/:rest*"} component={Monitoring} />
        <Route path={"/monitoramento"} component={Monitoring} />
        <Route path={"/"} component={Home} />
        <Route path={"/produtos"} component={Products} />
        <Route path={"/linha-prime"} component={CategorySolutions} />
        <Route path={"/portais-detectores-de-metais"} component={CategorySolutions} />
        <Route path={"/portas-automaticas"} component={CategorySolutions} />
        <Route path={"/produtos/portas-giratorias-hotelaria"} component={CategorySolutions} />
        <Route path={"/qual-porta-automatica-escolher"} component={CategorySolutions} />
        <Route path={"/aplicacoes/academias"} component={CategorySolutions} />
        <Route path={"/aplicacoes/controle-de-acesso-escolar"} component={CategorySolutions} />
        <Route path={"/produtos/:slug"} component={ProductDetail} />
        <Route path={"/solucoes/porta-deslizante-automatica"} component={SlidingDoorLanding} />
        <Route path={"/solucoes/eclusa-inspecao-remota"} component={EclusaRemoteInspectionLanding} />
        <Route path={"/sobre"} component={About} />
        <Route path={"/compliance"} component={() => { window.location.replace("/sobre#h-certificacoes"); return null; }} />
        <Route path={"/blog"} component={Blog} />
        <Route path={"/blog/:slug"} component={BlogPost} />
        <Route path={"/servicos"} component={Servicos} />
        <Route path={"/servicos/manutencao-preventiva"} component={ManutencaoPreventiva} />
        <Route path={"/servicos/manutencao-corretiva"} component={ManutencaoCorretiva} />
        <Route path={"/servicos/contratos-manutencao"} component={ContratosManutencao} />
        <Route path={"/servicos/instalacao-remanejo"} component={InstalacaoRemanejo} />
        <Route path={"/servicos/retrofit"} component={Retrofit} />
        <Route path={"/servicos/suporte-tecnico"} component={SuporteTecnico} />
        <Route path={"/aplicacoes"} component={Aplicacoes} />
        <Route path={"/aplicacoes/:slug"} component={SegmentoDetail} />
        <Route path={"/junte-se-a-nos"} component={RedirectToParceiros} />
        <Route path={"/parceiros"} component={Parceiros} />
        <Route path={"/trabalhe-conosco"} component={TrabalheCo} />
        <Route path={"/faq"} component={FAQPage} />
        <Route path={"/tecnologias"} component={Tecnologias} />
        <Route path={"/tecnologias/equisense"} component={EquiSensePage} />
        <Route path={"/tecnologias/equicorex"} component={EquiCorexPage} />
        <Route path={"/politica-de-privacidade"} component={PoliticaPrivacidade} />
        <Route path={"/politica-de-devolucao"} component={PoliticaDevolucao} />
        <Route path={"/termos-de-uso"} component={TermosDeUso} />
        <Route path={"/clientes"} component={Clientes} />
        <Route path={"/loja"} component={Loja} />
        <Route path={"/contato"} component={Contact} />
        <Route path={"/atualizacao-cadastral-tecnicos"} component={TechnicianRegistration} />
        <Route path={"/admin/login"} component={AdminLogin} />
        <Route path={"/recuperar-senha"} component={PasswordRecovery} />
        <Route path={"/redefinir-senha/:token"} component={PasswordReset} />
        <Route path={"/admin"} component={Admin} />
        <Route path={"/admin/:section"} component={Admin} />
        <Route path={"/convite/:token"} component={ConviteAceitar} />
        <Route path={"/404"} component={NotFound} />
        {/* Final fallback route */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider
        defaultTheme="light"
        // switchable
      >
        <TooltipProvider>
          <Toaster />
          <GoogleAnalytics />
          <WhatsAppLeadModal />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
